import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { ProductImportDetails, ProductImports as ProductImportsInterface } from 'src/types/productImports';
import genericActions, { emptyListingObject } from './common';
export interface ReduxStoreListObject {
  page: number,
  totalPages: number,
  totalCount: number,
  rows: ProductImportsInterface[],
  pageSize: number,
  skip: number,
}

export interface ReduxStoreListDetailObject {
  page: number,
  totalPages: number,
  totalCount: number,
  rows: ProductImportDetails[],
  pageSize: number,
  skip: number,
}
export interface ReduxStoreObject {
  records?: ReduxStoreListObject;
  selectedRecord?: ReduxStoreListDetailObject;
  error: boolean;
  success: boolean;
  message: string;
  loading: boolean;
  actionType?: string;
}

const initialState: ReduxStoreObject = {
  records: emptyListingObject,
  selectedRecord: emptyListingObject,
  error: false,
  success: false,
  message: '',
  loading: false,
  actionType: '',
};

const slice = createSlice({
  name: 'ProductImports',
  initialState,
  reducers: {
    getAll(state: ReduxStoreObject, action: PayloadAction<ReduxStoreObject>) {
      const { records, error, message, loading } = action.payload;
      state.records = records;
      state.error = error;
      state.message = message;
      state.loading = loading;
      state.actionType = 'getAll';
    },
    get(state: ReduxStoreObject, action: PayloadAction<ReduxStoreObject>) {
      const { selectedRecord, error, message, loading } = action.payload;
      if (selectedRecord) {
        state.selectedRecord = selectedRecord;
        state.error = error;
        state.message = message;
        state.loading = loading;
        state.actionType = 'get';
      }
    },
    set(state: ReduxStoreObject, action: PayloadAction<{ error: boolean; success: boolean; message: string; loading: boolean; }>) {
      const { success, error, message, loading } = action.payload;
      state.error = error;
      state.success = success;
      state.message = message;
      state.actionType = 'set';
      if (loading !== null)
        state.loading = loading;
    },
    remove(state: ReduxStoreObject, action: PayloadAction<{ error: boolean; success: boolean; message: string; loading: boolean; }>) {
      const { success, error, message, loading } = action.payload;
      state.error = error;
      state.success = success;
      state.message = message;
      state.loading = loading;
      state.actionType = 'remove';
    },
    resetError(state: ReduxStoreObject) {
      state.error = false;
      state.message = '';
      state.actionType = 'resetError';
    },
    resetSuccess(state: ReduxStoreObject) {
      state.success = false;
      state.message = '';
      state.actionType = 'resetSuccess';
    },
    setLoading(state: ReduxStoreObject, action: PayloadAction<{ loading: boolean; }>) {
      const { loading } = action.payload;
      state.loading = loading;
      state.actionType = 'loading';
    },
    resetSelected(state: ReduxStoreObject) {
      state.selectedRecord = emptyListingObject;
    },
    reset(state: ReduxStoreObject) {
      const { records, selectedRecord, success, error, loading, actionType, message } = initialState;
      state.records = records;
      state.selectedRecord = selectedRecord;
      state.error = error;
      state.success = success;
      state.message = message;
      state.actionType = actionType;
      state.loading = loading;
    },
  }
});

export const reducer = slice.reducer;

const resource = 'ProductImports';
export const { getAll, get, set, put, patch, remove, resetError, resetSuccess, reset } = genericActions(slice.actions, resource, emptyListingObject);
export default slice;